import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, ArrowRight, CheckCircle, Star, Shield, Users } from 'lucide-react';
import { ConsultationLayout } from '@/components/ConsultationLayout';
import { useAuth } from '@/contexts/AuthContext';
import { ConsultationService } from '@/integrations/supabase/services';
import { useToast } from '@/hooks/use-toast';
import '@/styles/animations.css';

const Consultation = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: user?.displayName || user?.email?.split('@')[0] || '',
    email: user?.email || '',
    phone: '',
    service: '',
    message: '',
    preferredDate: '',
    preferredTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Save consultation data to Firebase
      await ConsultationService.add({
        user_id: user?.uid || null,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        preferred_date: formData.preferredDate || null,
        preferred_time: formData.preferredTime,
        status: 'pending'
      });

      // Show success message
      toast({
        title: "Consultation Request Submitted",
        description: user 
          ? "Your consultation request has been saved. Redirecting to calendar..." 
          : "Your consultation request has been submitted successfully. We'll contact you soon to schedule your appointment.",
      });

      // Redirect to Notion calendar after a short delay (only for authenticated users)
      if (user) {
        setTimeout(() => {
          window.open('https://calendar.notion.so/meet/jenishmiranipol/817l03qnj', '_blank');
          setIsSubmitting(false);
        }, 1500);
      } else {
        // For non-authenticated users, just reset the form
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            message: '',
            preferredDate: '',
            preferredTime: ''
          });
          setIsSubmitting(false);
        }, 2000);
      }

    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const consultationTypes = [
    {
      title: "Initial Consultation",
      description: "Get started with your Polish residence card application",
      icon: <Star className="h-6 w-6" />,
      duration: "30 minutes",
      price: "Free"
    },
    {
      title: "Document Review",
      description: "Comprehensive review of your application documents",
      icon: <Shield className="h-6 w-6" />,
      duration: "45 minutes",
      price: "Basic Package"
    },
    {
      title: "Application Strategy",
      description: "Personalized strategy for your specific situation",
      icon: <Users className="h-6 w-6" />,
      duration: "60 minutes",
      price: "Standard Package"
    }
  ];

  // Service name mapping for dynamic text
  const getServiceDisplayName = (serviceValue: string) => {
    const serviceMap: { [key: string]: string } = {
      'temporary-residence': 'Temporary Residence Card',
      'permanent-residence': 'Permanent Residence Card',
      'eu-long-term': 'EU Long-term Residence Card',
      'work-permit': 'Work Permit',
      'company-registration': 'Company Registration',
      'business-incubator': 'Business Incubator',
      'translation-services': 'Translation Services',
      'general-consultation': 'General Consultation'
    };
    return serviceMap[serviceValue] || 'Polish residence card application';
  };

  // Dynamic description text
  const getDynamicDescription = () => {
    if (formData.service) {
      const serviceName = getServiceDisplayName(formData.service);
      return `Get personalized guidance for your ${serviceName} from our expert team`;
    }
    return 'Get personalized guidance for your Business and Immigration needs from our expert team';
  };

  const benefits = [
    "Expert guidance from immigration specialists",
    "Personalized application strategy",
    "Document checklist and requirements",
    "Timeline and process overview",
    "Q&A session for your specific concerns",
    "Next steps and recommendations"
  ];

  return (
    <ConsultationLayout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4 font-alumni">Schedule Your Consultation</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
            {getDynamicDescription()}
          </p>
        </div>


        <div className="grid lg:grid-cols-2 gap-8">
          {/* Consultation Form */}
          <Card className="backdrop-blur-sm animate-slide-in-left">
            <CardHeader>
              <CardTitle className="text-2xl font-alumni flex items-center gap-2">
                <Calendar className="h-6 w-6 text-primary" />
                Book Your Consultation
              </CardTitle>
              <CardDescription className="font-hind">
                Fill out the form below and we'll redirect you to our booking calendar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-hind">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-hind">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-hind">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="font-hind">Service of Interest *</Label>
                  <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="temporary-residence">Temporary Residence Card</SelectItem>
                      <SelectItem value="permanent-residence">Permanent Residence Card</SelectItem>
                      <SelectItem value="eu-long-term">EU Long-term Residence Card</SelectItem>
                      <SelectItem value="work-permit">Work Permit</SelectItem>
                      <SelectItem value="company-registration">Company Registration</SelectItem>
                      <SelectItem value="business-incubator">Business Incubator</SelectItem>
                      <SelectItem value="translation-services">Translation Services</SelectItem>
                      <SelectItem value="general-consultation">General Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="preferredDate" className="font-hind">Preferred Date</Label>
                    <Input
                      id="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferredTime" className="font-hind">Preferred Time</Label>
                    <Select value={formData.preferredTime} onValueChange={(value) => handleInputChange('preferredTime', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">Morning (9:00 AM - 12:00 PM)</SelectItem>
                        <SelectItem value="afternoon">Afternoon (12:00 PM - 5:00 PM)</SelectItem>
                        <SelectItem value="evening">Evening (5:00 PM - 8:00 PM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-hind">Additional Information</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell us about your situation, questions, or specific concerns..."
                    rows={4}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span>Schedule Consultation</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Consultation Types and Benefits */}
          <div className="space-y-6">
            {/* Consultation Types */}
            <Card className="backdrop-blur-sm animate-slide-in-right">
              <CardHeader>
                <CardTitle className="text-xl font-alumni flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Consultation Types
                </CardTitle>
                <CardDescription className="font-hind">
                  Choose the consultation that best fits your needs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {consultationTypes.map((type, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/50 transition-colors">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        {type.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold font-alumni">{type.title}</h4>
                        <p className="text-sm text-muted-foreground font-hind">{type.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {type.duration}
                          </span>
                          <span className="text-primary font-semibold">{type.price}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card className="backdrop-blur-sm animate-slide-in-right" style={{ animationDelay: '200ms' }}>
              <CardHeader>
                <CardTitle className="text-xl font-alumni flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  What You'll Get
                </CardTitle>
                <CardDescription className="font-hind">
                  Benefits of scheduling a consultation with us
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-hind">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="backdrop-blur-sm animate-slide-in-right" style={{ animationDelay: '400ms' }}>
              <CardHeader>
                <CardTitle className="text-xl font-alumni flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  Need Immediate Help?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-sm font-hind">info@sigmaimmigration.pl</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-primary" />
                    <span className="text-sm font-hind">+48 534 853 254</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm font-hind">Mon-Fri: 9:00 AM - 5:00 PM CET</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ConsultationLayout>
  );
};

export default Consultation; 